import React, { useRef, useState } from 'react';
import { owner as ownerApi, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, X, Calendar as CalIcon, Plus, ImagePlus, Trash2 } from 'lucide-react';

// A compact "Create event" modal used on the Events page so admins and
// business-owners don't have to detour into /manage/listings to publish
// something new. Submits to POST /api/my-events (which allows both roles).
// Kept intentionally minimal — the full editor with image upload + business
// linking still lives in ManageListings for anything richer.
const BLANK = {
  title: '', date: '', time: '', city: '', location: '', address: '',
  description: '', price: 0, capacity: 0, ticketUrl: '', image: '',
};

export default function CreateEventModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const imgInputRef = useRef(null);
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  if (!open) return null;

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast({ title: 'Please pick an image file' }); return; }
    if (file.size > 10 * 1024 * 1024)     { toast({ title: 'Image is too large (max 10MB)' }); return; }
    setUploading(true);
    try {
      const r = await filesApi.upload(file, 'post');
      setF('image', r.url);
      toast({ title: 'Cover image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
    } finally {
      setUploading(false);
      if (imgInputRef.current) imgInputRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast({ title: 'Event title is required' }); return; }
    if (!form.date)          { toast({ title: 'Date is required' }); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        price: form.price === '' ? 0 : Number(form.price) || 0,
        capacity: form.capacity === '' ? 0 : Math.max(0, Number(form.capacity) || 0),
        businessId: null,
      };
      const ev = await ownerApi.createEvent(payload);
      toast({ title: 'Event published', description: 'Awaiting quick admin review — it\'s already visible.' });
      setForm(BLANK);
      onCreated && onCreated(ev);
      onClose && onClose();
    } catch (err) {
      toast({ title: 'Create failed', description: err?.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto"
      data-testid="create-event-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
      <div className="bg-[#0d0d0d] border border-[#2a2a2a] rounded-2xl max-w-xl w-full my-8">
        <div className="p-5 border-b border-[#2a2a2a] flex items-start justify-between gap-3">
          <div>
            <div className="text-white font-bold text-lg inline-flex items-center gap-2">
              <CalIcon size={18} className="text-green-400"/> Create event
            </div>
            <div className="text-xs text-gray-400 mt-0.5">Publish an event to the PBB calendar. Free events reward attendees with points; paid events use Stripe checkout.</div>
          </div>
          <button type="button" onClick={onClose} data-testid="create-event-close"
            className="text-gray-400 hover:text-white p-1"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-3">
          <Field label="Event title *">
            <input value={form.title} onChange={(e) => setF('title', e.target.value)}
              data-testid="create-event-title" maxLength={120}
              placeholder="e.g. Black Business Mixer"
              className="pbb-input"/>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Date *">
              <input type="date" value={form.date} onChange={(e) => setF('date', e.target.value)}
                data-testid="create-event-date" className="pbb-input"/>
            </Field>
            <Field label="Time">
              <input type="time" value={form.time} onChange={(e) => setF('time', e.target.value)}
                data-testid="create-event-time" className="pbb-input"/>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="City">
              <input value={form.city} onChange={(e) => setF('city', e.target.value)}
                data-testid="create-event-city" placeholder="Atlanta"
                className="pbb-input"/>
            </Field>
            <Field label="Venue / Location">
              <input value={form.location} onChange={(e) => setF('location', e.target.value)}
                data-testid="create-event-location" placeholder="Rooftop Lounge"
                className="pbb-input"/>
            </Field>
          </div>

          <Field label="Address">
            <input value={form.address} onChange={(e) => setF('address', e.target.value)}
              data-testid="create-event-address" placeholder="123 Peachtree St"
              className="pbb-input"/>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Ticket price (USD)" hint="0 = free (points reward)">
              <input type="number" min={0} step="0.01" value={form.price}
                onChange={(e) => setF('price', e.target.value)}
                data-testid="create-event-price" className="pbb-input"/>
            </Field>
            <Field label="Capacity" hint="0 = unlimited">
              <input type="number" min={0} value={form.capacity}
                onChange={(e) => setF('capacity', e.target.value)}
                data-testid="create-event-capacity" className="pbb-input"/>
            </Field>
          </div>

          <Field label="Cover image" hint="Upload a photo from your device. JPG/PNG/WebP up to 10MB.">
            <div>
              <input ref={imgInputRef} type="file" accept="image/*" className="hidden"
                data-testid="create-event-image-file"
                onChange={(e) => uploadImage(e.target.files?.[0])}/>
              {!form.image ? (
                <button type="button" onClick={() => imgInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="create-event-image-upload"
                  className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg border border-dashed border-[#3a3a3a] hover:border-green-500 bg-black text-sm text-gray-300 hover:text-white disabled:opacity-60">
                  {uploading ? <><Loader2 size={14} className="animate-spin"/> Uploading…</> : <><ImagePlus size={14}/> Upload cover image</>}
                </button>
              ) : (
                <div className="relative rounded-lg overflow-hidden border border-[#2a2a2a] bg-black" data-testid="create-event-image-preview">
                  <img src={filesApi.urlFor(form.image)} alt="Cover preview" className="w-full h-40 object-cover"/>
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button type="button" onClick={() => imgInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="create-event-image-replace"
                      className="text-[11px] px-2 py-1 rounded bg-black/70 border border-[#3a3a3a] text-white hover:border-green-500 inline-flex items-center gap-1 disabled:opacity-60">
                      {uploading ? <Loader2 size={11} className="animate-spin"/> : <ImagePlus size={11}/>} Replace
                    </button>
                    <button type="button" onClick={() => setF('image', '')}
                      data-testid="create-event-image-remove"
                      className="text-[11px] px-2 py-1 rounded bg-black/70 border border-[#3a3a3a] text-white hover:border-red-500 inline-flex items-center gap-1">
                      <Trash2 size={11}/> Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Field>

          <Field label="Description">
            <textarea value={form.description} onChange={(e) => setF('description', e.target.value)}
              data-testid="create-event-description" rows={4} maxLength={2000}
              placeholder="What to expect, dress code, agenda…"
              className="pbb-input"/>
          </Field>
        </div>

        <div className="p-5 pt-0 flex gap-2">
          <button type="button" onClick={onClose} disabled={saving} data-testid="create-event-cancel"
            className="flex-1 px-3 py-2 rounded-lg border border-[#2a2a2a] hover:border-gray-500 text-gray-200 text-sm">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving || uploading} data-testid="create-event-submit"
            className="flex-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm inline-flex items-center justify-center gap-1 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Publish event
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[11px] text-gray-400 mb-1 font-semibold uppercase tracking-wider">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-gray-500 mt-1">{hint}</div>}
    </label>
  );
}
